import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Chip, Link } from "@mui/material";
import React from "react";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import { TableCell, TableRow } from "@/components/molecules/Table/styled";
import useCompanyPaymentData from "@/hooks/data/use-company-payment-data";
import { ICustomerSalesProductPayment } from "@/interfaces/customer-sales-product-payment.interfaces";
import { CompanyPaymentsMessages } from "@/messages/customer/company-payments.messages";
import { CustomerSalesProductPaymentStatus } from "@/types/customer-sales-product-payment-status.types";

interface Props {
  payment: ICustomerSalesProductPayment;
  index: number;
  messages: CompanyPaymentsMessages;
}

const PAYMENT_ACTION_MIN_WIDTH = 120;

function PaymentActionCell({
  payment,
  formattedPaymentDate,
  payInvoiceLabel,
}: {
  payment: ICustomerSalesProductPayment;
  formattedPaymentDate: string;
  payInvoiceLabel: string;
}) {
  if (payment.status === CustomerSalesProductPaymentStatus.Paid) {
    return (
      <MDTypography variant="body2" component="span">
        {formattedPaymentDate}
      </MDTypography>
    );
  }

  if (payment.invoiceUrl) {
    return (
      <Link
        href={payment.invoiceUrl}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          fontSize: "0.875rem",
          fontWeight: 600,
          whiteSpace: "nowrap",
        }}
      >
        {payInvoiceLabel}
        <OpenInNewIcon sx={{ fontSize: 14 }} />
      </Link>
    );
  }

  return (
    <MDTypography variant="body2" color="text" component="span">
      -
    </MDTypography>
  );
}

export function CompanyPaymentTableRow({ payment, index, messages }: Props) {
  const paymentData = useCompanyPaymentData({ payment, messages });

  return (
    <TableRow index={index}>
      <TableCell align="left">{paymentData.salesProductName || "-"}</TableCell>
      <TableCell align="left">{paymentData.salesProductPricePlanName || "-"}</TableCell>
      <TableCell align="center">
        <Chip
          label={paymentData.statusLabel}
          size="small"
          color={paymentData.statusChipColor}
          variant="outlined"
        />
      </TableCell>
      <TableCell align="center">{paymentData.formattedDueDate}</TableCell>
      <TableCell align="right">{paymentData.formattedValue}</TableCell>
      <TableCell
        align="center"
        sx={{
          minWidth: PAYMENT_ACTION_MIN_WIDTH,
          verticalAlign: "middle",
        }}
      >
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={32}
          width="100%"
        >
          <PaymentActionCell
            payment={payment}
            formattedPaymentDate={paymentData.formattedPaymentDate}
            payInvoiceLabel={messages.payInvoiceButton}
          />
        </MDBox>
      </TableCell>
    </TableRow>
  );
}

export default CompanyPaymentTableRow;
